import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import { SimpleResult } from "../dto";
import {
  DialogType,
  IDialog,
  IDialogOrder,
  IDialogProps,
  IDialogVariant,
} from "../models";
import { dialogsAtom } from "../recoil";

interface IOpenDialogArguments {
  dialogName: DialogType;
  dialogProps: IDialogProps;
  order?: IDialogOrder;
  variant?: IDialogVariant;
}

export const useDialogs = (): {
  openDialog: (args: IOpenDialogArguments) => void;
  closeDialog: (name: string) => void;
  dialogs: IDialog[];
  openAsyncDialog: <T>({
    dialogName,
    dialogProps,
  }: {
    dialogName: DialogType;
    dialogProps: IDialogProps;
  }) => Promise<SimpleResult<T>>;
} => {
  const [dialogs, setDialog] = useRecoilState(dialogsAtom);

  const openDialog = useEvent(
    ({
      dialogName,
      dialogProps,
      order = "default",
      variant = "default",
    }: IOpenDialogArguments): void => {
      setDialog(oldDialogs => {
        return [...oldDialogs, { dialogName, dialogProps, order, variant }];
      });
    },
  );

  const closeDialog = useEvent(dialogNameToClose => {
    setDialog(oldDialogs => {
      const lastIndex = oldDialogs.findLastIndex(
        ({ dialogName }) => dialogName === dialogNameToClose,
      );

      return oldDialogs.toSpliced(lastIndex, 1);
    });
  });

  const openAsyncDialog = useEvent(
    async <T>({
      dialogName,
      dialogProps,
    }: {
      dialogName: DialogType;
      dialogProps: IDialogProps;
    }): Promise<SimpleResult<T>> => {
      return new Promise((resolve, reject) => {
        openDialog({
          dialogName,
          dialogProps: {
            ...dialogProps,
            resolveFn: resolve,
            rejectFn: reject,
          },
        });
      });
    },
  );

  return { openDialog, closeDialog, dialogs, openAsyncDialog };
};
